// Generated by scripts/sync-quote-review-contract.mjs. Do not edit by hand.
import { z } from 'zod';

const id = z.string().uuid();
const instant = z.string().datetime();
const money = z.number().finite(); // net cents retain the existing four-decimal precision
const label = z.object({ id, name: z.string() });
const labor = z.object({
  id,
  description: z.string(),
  estimatedHours: z.number(),
  discountPercent: z.number(),
  sortOrder: z.number(),
});
const part = z.object({
  id,
  name: z.string(),
  quantity: z.number(),
  unitPriceCents: money,
  vatPercent: z.number(),
  discountPercent: z.number(),
  notes: z.string().nullable(),
});
const extra = z.object({
  id,
  description: z.string(),
  amountCents: money,
  vatPercent: z.number(),
  isTaxable: z.boolean(),
  hasVariableQuantity: z.boolean(),
  quantity: z.number().nullable(),
  unit: z.string().nullable(),
});

export const quotePriceLineSchema = z.object({
  lineType: z.enum(['labor', 'part', 'service', 'surcharge', 'discount']),
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitPriceCents: money,
  discountPercent: z.number(),
  lineTotalCents: money,
  taxable: z.boolean(),
  taxPercent: z.number().nullable(),
  groupName: z.string().nullable(),
  groupSortOrder: z.number(),
  groupNotes: z.string().nullable().optional(),
  workOrderItemId: id.optional(),
  workOrderPartId: id.optional(),
  bookingServiceId: id.optional(),
  laborItemIds: z.array(id).optional(),
  sortOrder: z.number(),
});

/** Complete review only: the backend refuses oversize projections instead of truncating scope. */
export const quoteReviewSchema = z.object({
  snapshotVersion: z.literal(1),
  complete: z.literal(true),
  id,
  workOrderNumber: z.string(),
  status: z.string().min(1),
  updatedAt: instant,
  quoteRevision: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  workshopTimezone: z.string(),
  quoteExpiresAt: instant.nullable(),
  isDisputed: z.boolean(),
  allowedActions: z.array(z.enum(['accept', 'reject'])),
  vehicle: z.object({
    id,
    registrationNumber: z.string(),
    make: z.string().nullable(),
    model: z.string().nullable(),
  }),
  orderer: z
    .object({
      partyType: z.enum(['person', 'company']),
      personId: id.nullable(),
      companyId: id.nullable(),
      displayName: z.string(),
    })
    .nullable(),
  parties: z.array(
    z.object({
      id,
      role: z.string(),
      partyType: z.enum(['person', 'company']),
      personId: id.nullable(),
      companyId: id.nullable(),
      displayName: z.string(),
    })
  ),
  schedule: z.object({
    scheduledAt: instant,
    schedulingType: z.enum(['exact', 'floating']),
    estimatedDurationHours: z.number().nullable(),
    customerRequestedPickupAt: instant.nullable(),
    customerWaits: z.boolean(),
    allowMinorMaintenance: z.boolean(),
    jointInspectionRequested: z.boolean(),
    preapprovedAdditionalWorkLimitCents: z.number().int().nullable(),
    serviceScheduling: z.string().nullable(),
  }),
  resources: z.object({
    assignedMechanicId: id.nullable(),
    assignedLiftId: id.nullable(),
    assignedMechanic: label.nullable(),
    assignedLift: label.nullable(),
  }),
  services: z.array(
    z.object({
      bookingServiceId: id,
      serviceId: id,
      name: z.string(),
      pricingType: z.string(),
      fixedPriceCents: money.nullable(),
      fixedPriceOverrideCents: money.nullable(),
      plannedHours: z.number().nullable(),
      plannedHoursManual: z.boolean(),
      scheduledAt: instant.nullable(),
      assignedMechanicId: id.nullable(),
      assignedLiftId: id.nullable(),
      assignedMechanic: label.nullable(),
      assignedLift: label.nullable(),
      notes: z.string().nullable(),
      laborItems: z.array(labor),
      parts: z.array(part),
      extras: z.array(extra),
    })
  ),
  legacyItems: z.array(
    z.object({
      id,
      description: z.string(),
      laborCostCents: money,
      partsCostCents: money,
      vatPercent: z.number().nullable(),
      estimatedHours: z.number().nullable(),
      status: z.string(),
    })
  ),
  legacyParts: z.array(
    z.object({
      id,
      partName: z.string(),
      quantity: z.number(),
      unit: z.string(),
      unitSellPriceCents: money,
      discountPercent: z.number(),
      workOrderItemId: id.nullable(),
    })
  ),
  customerNotes: z.string().nullable(),
  pricing: z.object({
    currency: z.literal('EUR'),
    defaultVatPercent: z.number(),
    laborRateCents: money,
    surchargeCents: money,
    netSubtotalCents: z.number().int(),
    taxCents: z.number().int(),
    grossTotalCents: z.number().int(),
    lines: z.array(quotePriceLineSchema),
    surcharge: z.object({
      type: z.string().nullable(),
      percentage: z.number().nullable(),
      fixedCents: money.nullable(),
    }),
  }),
});

/** What the staff member attests before the receipt time is confirmed (#2821). */
export const quoteApprovalAttestationSchema = z
  .object({
    statement: z.string().trim().min(1).max(2000),
    customerName: z.string().trim().min(1).max(200),
    channel: z.enum(['phone', 'in_person', 'email', 'sms', 'whatsapp', 'other']),
    reference: z.string().trim().min(1).max(500).optional(),
  })
  .strict();
/** The staff member asserts receipt; this is not independently verified customer consent. */
export const quoteCustomerApprovalSchema = quoteApprovalAttestationSchema
  .extend({
    receivedAt: instant,
    /**
     * Additive (#2821): the receipt time was confirmed by the staff member on
     * the confirmation card, with the pre-fill it started from (null when
     * none) and whether they changed it. Absent on legacy decisions.
     */
    receivedAtSource: z.literal('staff_confirmed').optional(),
    receivedAtPrefill: instant.nullable().optional(),
    receivedAtEdited: z.boolean().optional(),
  })
  .strict();
export const quoteDecisionReviewSchema = z
  .object({
    quoteRevision: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    actorConfirmationId: id,
    customerApproval: quoteCustomerApprovalSchema.optional(),
  })
  .strict();

export type QuoteReview = z.infer<typeof quoteReviewSchema>;
export type QuoteDecisionReview = z.infer<typeof quoteDecisionReviewSchema>;
export type QuoteCustomerApproval = z.infer<typeof quoteCustomerApprovalSchema>;

export const quoteSearchResponseSchema = z.object({
  quotes: z.array(
    z.object({
      id,
      workOrderNumber: z.string(),
      status: z.enum(['quote', 'quote_rejected']),
      updatedAt: instant,
      createdAt: instant,
      quoteExpiresAt: instant.nullable(),
      isDisputed: z.boolean(),
      vehicleRegistration: z.string(),
      customerName: z.string().nullable(),
    })
  ),
  pagination: z.object({ limit: z.number().int(), offset: z.number().int(), hasMore: z.boolean() }),
  serverNow: instant,
});
export type QuoteSearchResponse = z.infer<typeof quoteSearchResponseSchema>;

/*
 * Staff-confirmed quote-approval receipt time (#2821).
 *
 * The receipt time recorded in the quote-decision audit is part of the staff
 * attestation, so it must be what the staff member CONFIRMED, not a model
 * guess or a regex reading of their chat message. The orchestrator only
 * pre-fills it (`quoteReceiptPrefillSchema`); the confirmation card asks for
 * a civil date and time in the WORKSHOP timezone and the confirm request
 * carries the resulting instant with its offset.
 *
 * This file is mirrored verbatim into the staff portal by
 * `apps/staff-portal/scripts/sync-quote-review-contract.mjs` (imports are
 * stripped), so it must stay self-contained: zod and `Intl` only. The card
 * and the orchestrator resolve civil times with the SAME functions below.
 */

/** Nothing before this is a plausible receipt time for a quote in this product. */
export const QUOTE_RECEIPT_EARLIEST_MS = Date.UTC(2000, 0, 1);

/** Why the orchestrator could (`ok`) or could not pre-fill the receipt time. */
export const quoteReceiptPrefillStatusSchema = z.enum([
  'ok',
  /** No date and time stated in the acceptance sentence. */
  'missing',
  /** More than one candidate, an alternative or a range. */
  'ambiguous',
  /** Today/yesterday-style wording. */
  'relative',
  /** around/noin-style wording. */
  'approximate',
  /** An impossible date/time, offset or unparsed suffix. */
  'invalid',
  /** The stated civil time is skipped by a DST change. */
  'nonexistent',
  /** The stated civil time occurs twice (DST fall-back) and names no offset. */
  'repeated',
  /** The stated time is in the future. */
  'future',
  /** The approval statement could not be located as one sentence of the message. */
  'unlocated',
]);
export type QuoteReceiptPrefillStatus = z.infer<typeof quoteReceiptPrefillStatusSchema>;

/** The receipt pre-fill a quote acceptance card starts from. Never evidence by itself. */
export const quoteReceiptPrefillSchema = z
  .object({
    receivedAt: z.string().datetime().nullable(),
    status: quoteReceiptPrefillStatusSchema,
  })
  .strict()
  .refine((value) => (value.status === 'ok') === (value.receivedAt !== null), {
    message: 'A receipt pre-fill carries an instant exactly when its status is ok',
  });
export type QuoteReceiptPrefill = z.infer<typeof quoteReceiptPrefillSchema>;

/** The staff-confirmed instant on the confirm request: ISO 8601 WITH an explicit offset. */
export const quoteConfirmedReceivedAtSchema = z.string().datetime({ offset: true });

export type QuoteReceiptInstantCheck = 'ok' | 'invalid' | 'future' | 'too_early';

const QUOTE_CONFIRMED_INSTANT =
  /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d)(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|([+-])(\d{2}):(\d{2}))$/;

/**
 * THE check for a staff-confirmed receipt instant — the card, the handler's
 * pre-dispatch gate and the tool's `run` all call it (#2821). It accepts only
 * what the card can produce:
 *  - minute resolution (seconds and fractions, if written, are zero);
 *  - an explicit offset within ±14:00, minutes < 60;
 *  - an offset that IS the workshop zone's offset at that instant, so a civil
 *    time and offset that do not belong together are refused, and a repeated
 *    autumn hour is accepted only as one of its two real occurrences;
 *  - not before 2000 and not later than `now` (no clock-skew allowance:
 *    staff-api refuses `receivedAt > now` too; the current minute is valid).
 */
export function checkQuoteReceiptInstant(
  iso: string,
  now: number,
  timeZone: string
): QuoteReceiptInstantCheck {
  const match = QUOTE_CONFIRMED_INSTANT.exec(iso);
  if (!match) return 'invalid';
  const [, y, mo, d, h, mi, s, fraction, zone, sign, oh, om] = match;
  if (Number(s ?? '0') !== 0 || Number(fraction ?? '0') !== 0) return 'invalid';
  let offsetMs = 0;
  if (zone !== 'Z') {
    const hours = Number(oh);
    const minutes = Number(om);
    if (hours > 14 || minutes > 59 || (hours === 14 && minutes > 0)) return 'invalid';
    offsetMs = (hours * 60 + minutes) * 60_000 * (sign === '-' ? -1 : 1);
  }
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const civil = quoteUtcMs(year, month, day, Number(h), Number(mi));
  const probe = new Date(civil);
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  )
    return 'invalid';
  const formatter = quoteZoneFormatter(timeZone);
  if (!formatter) return 'invalid';
  const ms = civil - offsetMs;
  // The workshop wall clock at that instant must read exactly the civil time
  // written, i.e. the offset is the zone's own offset then.
  if (quoteWallClockMs(ms, formatter) !== civil) return 'invalid';
  if (ms < QUOTE_RECEIPT_EARLIEST_MS) return 'too_early';
  if (ms > now) return 'future';
  return 'ok';
}

const QUOTE_CIVIL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const QUOTE_CIVIL_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

function quoteZoneFormatter(timeZone: string): Intl.DateTimeFormat | undefined {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return undefined;
  }
}

/** UTC milliseconds of a civil wall clock, without `Date.UTC`'s 0–99 → 19xx year mapping. */
function quoteUtcMs(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0
): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, 0);
  return date.getTime();
}

/** The zone's wall clock at `instant`, as UTC milliseconds of that wall clock. */
function quoteWallClockMs(instant: number, formatter: Intl.DateTimeFormat): number {
  const parts = formatter.formatToParts(new Date(instant));
  const pick = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return quoteUtcMs(
    pick('year'),
    pick('month'),
    pick('day'),
    pick('hour'),
    pick('minute'),
    pick('second')
  );
}

/** One instant at which a zone shows a civil date and time, with the offset then in force. */
export interface QuoteCivilCandidate {
  /** Zone offset in milliseconds (east of UTC positive). */
  readonly ms: number;
  readonly instant: number;
}

/**
 * Every instant at which `timeZone` shows civil `year-month-day hour:minute`:
 * none in a spring-forward gap, two in an autumn fall-back hour, one
 * otherwise, sorted by instant. Offsets are read from `Intl` one day either
 * side; there is no DST table. An unknown zone yields none.
 */
export function quoteCivilTimeCandidates(
  day: { readonly year: number; readonly month: number; readonly day: number },
  hour: number,
  minute: number,
  timeZone: string
): QuoteCivilCandidate[] {
  const formatter = quoteZoneFormatter(timeZone);
  if (!formatter) return [];
  const wanted = quoteUtcMs(day.year, day.month, day.day, hour, minute);
  const offsets = new Set(
    [-86_400_000, 0, 86_400_000].map(
      (delta) => quoteWallClockMs(wanted + delta, formatter) - (wanted + delta)
    )
  );
  return [...offsets]
    .map((ms) => ({ ms, instant: wanted - ms }))
    .filter((candidate) => quoteWallClockMs(candidate.instant, formatter) === wanted)
    .sort((a, b) => a.instant - b.instant);
}

/** `+03:00` for an offset in milliseconds. */
export function formatQuoteUtcOffset(ms: number): string {
  const minutes = Math.round(Math.abs(ms) / 60_000);
  const sign = ms < 0 ? '-' : '+';
  return `${sign}${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** An instant as ISO with the given offset, e.g. `2026-10-03T09:15:00+03:00`. */
export function quoteInstantWithOffset(instant: number, offsetMs: number): string {
  return `${new Date(instant + offsetMs).toISOString().slice(0, 19)}${formatQuoteUtcOffset(offsetMs)}`;
}

/** The civil `YYYY-MM-DD` and `HH:MM` an instant shows in `timeZone`, or undefined. */
export function quoteCivilOf(
  iso: string,
  timeZone: string
): { readonly date: string; readonly time: string } | undefined {
  const ms = Date.parse(iso);
  const formatter = quoteZoneFormatter(timeZone);
  if (!Number.isFinite(ms) || !formatter) return undefined;
  const wall = new Date(quoteWallClockMs(ms, formatter)).toISOString();
  return { date: wall.slice(0, 10), time: wall.slice(11, 16) };
}

export type QuoteReceiptCivilResolution =
  | { readonly kind: 'ok'; readonly receivedAt: string }
  | {
      readonly kind: 'repeated';
      /** Both occurrences, earlier first, each as ISO with its own offset. */
      readonly options: readonly { readonly receivedAt: string; readonly offset: string }[];
    }
  | { readonly kind: 'incomplete' | 'invalid' | 'nonexistent' | 'future' | 'too_early' };

/**
 * A civil date + time entered in the WORKSHOP zone → the instant the staff
 * member confirms, as ISO with that zone's offset. A skipped time is
 * `nonexistent`; a repeated one is `repeated` unless `chosen` names one of
 * its two occurrences; later than `now` is `future`.
 */
export function resolveQuoteReceiptCivilTime(
  date: string,
  time: string,
  timeZone: string,
  now: number,
  chosen?: string
): QuoteReceiptCivilResolution {
  if (date === '' || time === '') return { kind: 'incomplete' };
  const dateMatch = QUOTE_CIVIL_DATE.exec(date);
  const timeMatch = QUOTE_CIVIL_TIME.exec(time);
  if (!dateMatch || !timeMatch || !quoteZoneFormatter(timeZone)) return { kind: 'invalid' };
  const day = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
  };
  const probe = new Date(quoteUtcMs(day.year, day.month, day.day));
  if (
    probe.getUTCFullYear() !== day.year ||
    probe.getUTCMonth() !== day.month - 1 ||
    probe.getUTCDate() !== day.day
  )
    return { kind: 'invalid' };
  const candidates = quoteCivilTimeCandidates(
    day,
    Number(timeMatch[1]),
    Number(timeMatch[2]),
    timeZone
  );
  if (candidates.length === 0) return { kind: 'nonexistent' };
  const options = candidates.map((candidate) => ({
    receivedAt: quoteInstantWithOffset(candidate.instant, candidate.ms),
    offset: formatQuoteUtcOffset(candidate.ms),
  }));
  const receivedAt =
    options.length === 1
      ? options[0]?.receivedAt
      : options.find(
          (option) => chosen !== undefined && Date.parse(option.receivedAt) === Date.parse(chosen)
        )?.receivedAt;
  if (receivedAt === undefined) return { kind: 'repeated', options };
  const bounds = checkQuoteReceiptInstant(receivedAt, now, timeZone);
  return bounds === 'ok' ? { kind: 'ok', receivedAt } : { kind: bounds };
}

/** The complete effective schedule shown by a quote confirmation, not a PATCH. */
export const quoteAcceptanceReviewSchema = z
  .object({
    scheduledAt: z.string().datetime(),
    schedulingType: z.enum(['exact', 'floating']),
    customerRequestedPickupAt: z.string().datetime().nullable(),
    estimatedDurationHours: z.number().finite().nonnegative().nullable(),
    customerWaits: z.boolean(),
    allowMinorMaintenance: z.boolean(),
    jointInspectionRequested: z.boolean(),
    preapprovedAdditionalWorkLimitCents: z.number().int().nonnegative().nullable(),
  })
  .strict();

/**
 * Additive card protocol. An unreadable review must disable confirmation.
 *
 * Acceptance is version 2 (#2821): the attestation carries no receipt time;
 * `receipt` is only the card's pre-fill, and the staff member confirms the
 * time on the card. A version-1 acceptance (model-supplied receipt time) is
 * no longer readable, so such a card cannot be confirmed.
 */
export const quoteConfirmationReviewSchema = z.discriminatedUnion('action', [
  z
    .object({
      version: z.literal(2),
      action: z.literal('accept'),
      quote: quoteReviewSchema,
      customerApproval: quoteApprovalAttestationSchema,
      receipt: quoteReceiptPrefillSchema,
      acceptance: quoteAcceptanceReviewSchema,
    })
    .strict(),
  z
    .object({
      version: z.literal(1),
      action: z.literal('reject'),
      quote: quoteReviewSchema,
      reason: z.string().trim().min(1).max(2000),
    })
    .strict(),
]);

export type QuoteConfirmationReview = z.infer<typeof quoteConfirmationReviewSchema>;

/*
 * Reviewed edit/removal of one existing quote line (#2546).
 *
 * Mirrored verbatim into the staff portal by
 * `apps/staff-portal/scripts/sync-quote-review-contract.mjs` (imports are
 * stripped), so it must stay self-contained: zod and the quote review schema
 * defined before it in that mirror.
 *
 * The editable field set is what the portal Payments card lets staff edit on a
 * `work_order_items` line (`WORK_ORDER_ITEM_CAPS`): the net labor and net parts
 * amounts, and deletion. Quantity, discount, VAT and description have no
 * portal editor for these lines and are not part of this contract.
 */

/** Same bound as `MAX_QUOTE_ITEM_CENTS`: the cost columns are numeric(12,4). */
export const QUOTE_ITEM_CHANGE_MAX_CENTS = 99_999_999;
const quoteItemChangeCents = z.number().int().min(0).max(QUOTE_ITEM_CHANGE_MAX_CENTS);
const quoteItemChangeRevision = z.string().regex(/^sha256:[a-f0-9]{64}$/);

/** The exact intended change. Both amounts are explicit: nothing is defaulted by the server. */
export const quoteItemChangeCommandSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('update'),
      laborCostCents: quoteItemChangeCents,
      partsCostCents: quoteItemChangeCents,
    })
    .strict(),
  z.object({ action: z.literal('remove') }).strict(),
]);

/** The consent token: current version + material revision + the reviewed resulting state. */
export const quoteItemChangeTokenSchema = z
  .object({
    quoteRevision: quoteItemChangeRevision,
    updatedAt: z.string().datetime(),
    resultRevision: quoteItemChangeRevision,
  })
  .strict();

export const reviewedQuoteItemUpdateSchema = z
  .object({
    laborCostCents: quoteItemChangeCents,
    partsCostCents: quoteItemChangeCents,
    review: quoteItemChangeTokenSchema,
  })
  .strict();
export const reviewedQuoteItemRemoveSchema = z
  .object({ review: quoteItemChangeTokenSchema })
  .strict();

const quoteItemChangeLine = quoteReviewSchema.shape.legacyItems.element;

/**
 * Server-built review of one change: the current complete quote, the targeted
 * line before, the COMPLETE resulting line set and totals, and which fields
 * change. A missing or unreadable review must disable confirmation.
 */
export const quoteItemChangeReviewSchema = z
  .object({
    version: z.literal(1),
    itemId: z.string().uuid(),
    command: quoteItemChangeCommandSchema,
    quote: quoteReviewSchema,
    before: quoteItemChangeLine,
    after: quoteItemChangeLine.nullable(),
    changedFields: z.array(z.enum(['laborCostCents', 'partsCostCents', 'removed'])).min(1),
    result: z
      .object({
        legacyItems: quoteReviewSchema.shape.legacyItems,
        pricing: quoteReviewSchema.shape.pricing,
      })
      .strict(),
    resultRevision: quoteItemChangeRevision,
  })
  .strict()
  .superRefine((review, ctx) => {
    const removed = review.command.action === 'remove';
    if (removed !== (review.after === null) || removed !== review.changedFields.includes('removed'))
      ctx.addIssue({ code: 'custom', message: 'removal must have no resulting line' });
    if (review.before.id !== review.itemId || (review.after && review.after.id !== review.itemId))
      ctx.addIssue({ code: 'custom', message: 'review targets another line' });
  });

/** The reviewed write's 200 body. */
export const quoteItemChangeResultSchema = z
  .object({
    action: z.enum(['update', 'remove']),
    workOrderId: z.string().uuid(),
    itemId: z.string().uuid(),
    item: quoteItemChangeLine.nullable(),
    updatedAt: z.string().datetime(),
    totals: z
      .object({
        netSubtotalCents: z.number().int(),
        taxCents: z.number().int(),
        grossTotalCents: z.number().int(),
      })
      .strict(),
  })
  .strict();

export type QuoteItemChangeCommand = z.infer<typeof quoteItemChangeCommandSchema>;
export type QuoteItemChangeToken = z.infer<typeof quoteItemChangeTokenSchema>;
export type QuoteItemChangeReview = z.infer<typeof quoteItemChangeReviewSchema>;
export type QuoteItemChangeResult = z.infer<typeof quoteItemChangeResultSchema>;

/*
 * Reviewed chat edit of the lines of one catalog service on a quote (#2546,
 * design A1: docs/staff-agent-parity/service-line-editing-design.md §4.3, §5.1).
 *
 * Mirrored verbatim into the staff portal by
 * `apps/staff-portal/scripts/sync-quote-review-contract.mjs` (imports are
 * stripped), so it must stay self-contained: zod and the quote review schema
 * defined before it in that mirror.
 *
 * Operations are an internal contract. The model never authors them as JSON:
 * the orchestrator expands one flat tool call into operations and mints every
 * `newLineId`. Every money input is VAT-inclusive whole cents; the server
 * converts it once with the same rule the portal uses.
 */

/** Same bound as the portal PUT: the 4 dp money columns are numeric(12,4). */
export const QUOTE_SERVICE_LINE_MAX_CENTS = 99_999_999;
/** `numeric(5,2)`: labour hours. */
export const QUOTE_SERVICE_LINE_MAX_HOURS = 999.99;
/** `numeric(10,2)`: part and extra quantities. */
export const QUOTE_SERVICE_LINE_MAX_QUANTITY = 99_999_999.99;
/** After normalization: one confirmation never carries more. */
export const QUOTE_SERVICE_LINE_MAX_OPS = 20;
/** Before normalization (folds can shrink a list). */
export const QUOTE_SERVICE_LINE_MAX_INPUT_OPS = 100;
export const QUOTE_SERVICE_LINE_VAT_DECIMALS = 2;
export const QUOTE_SERVICE_LINE_DISCOUNT_DECIMALS = 1;
/** Part VAT rates when the workshop has configured none: the portal's own fallback list. */
export const QUOTE_SERVICE_LINE_FALLBACK_VAT_PERCENTS = [25.5, 14, 10, 0] as const;

/**
 * Digits after the decimal point of the shortest round-trip representation of
 * the number itself, checked on the INPUT, before any storage round trip:
 * 25.50000001 has 8 (a float4 read-back would show 25.5). An exponent form
 * (1e-7, 1e21) is never representable.
 */
export function quoteServiceLineDecimals(value: number): number {
  if (!Number.isFinite(value)) return Number.POSITIVE_INFINITY;
  const text = String(Math.abs(value));
  if (/e/i.test(text)) return Number.POSITIVE_INFINITY;
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

const serviceLineId = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'lowercase_uuid');
const serviceLineText = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .refine((value) => value.trim().length > 0, 'blank_text');
const serviceLineGross = z.number().int().min(0).max(QUOTE_SERVICE_LINE_MAX_CENTS);
const serviceLineHours = z
  .number()
  .min(0)
  .max(QUOTE_SERVICE_LINE_MAX_HOURS)
  .refine((value) => quoteServiceLineDecimals(value) <= 2, 'hours_precision');
const serviceLinePartQuantity = z
  .number()
  .min(0.01)
  .max(QUOTE_SERVICE_LINE_MAX_QUANTITY)
  .refine((value) => quoteServiceLineDecimals(value) <= 2, 'quantity_precision');
/** A null, zero or negative per-unit quantity would bill as 1, so it is refused. */
const serviceLineExtraQuantity = z
  .number()
  .gt(0)
  .max(QUOTE_SERVICE_LINE_MAX_QUANTITY)
  .refine((value) => quoteServiceLineDecimals(value) <= 2, 'quantity_precision');
const serviceLineDiscount = z
  .number()
  .min(0)
  .max(100)
  .refine(
    (value) => quoteServiceLineDecimals(value) <= QUOTE_SERVICE_LINE_DISCOUNT_DECIMALS,
    'discount_precision'
  );
const serviceLineVat = z
  .number()
  .min(0)
  .max(100)
  .refine(
    (value) => quoteServiceLineDecimals(value) <= QUOTE_SERVICE_LINE_VAT_DECIMALS,
    'vat_rate_not_representable'
  );
const serviceLineDescription = serviceLineText(2000);

/** One typed change. `lineId` names an existing row; `newLineId` is minted for an add. */
export const serviceLineOpSchema = z.discriminatedUnion('op', [
  z
    .object({
      op: z.literal('labor.update'),
      bookingServiceId: serviceLineId,
      lineId: serviceLineId,
      description: serviceLineDescription.optional(),
      estimatedHours: serviceLineHours.optional(),
      discountPercent: serviceLineDiscount.optional(),
    })
    .strict(),
  z
    .object({
      op: z.literal('labor.add'),
      bookingServiceId: serviceLineId,
      newLineId: serviceLineId,
      description: serviceLineDescription,
      estimatedHours: serviceLineHours,
      discountPercent: serviceLineDiscount,
    })
    .strict(),
  z
    .object({ op: z.literal('labor.remove'), bookingServiceId: serviceLineId, lineId: serviceLineId })
    .strict(),
  z
    .object({
      op: z.literal('part.update'),
      bookingServiceId: serviceLineId,
      lineId: serviceLineId,
      name: serviceLineText(200).optional(),
      quantity: serviceLinePartQuantity.optional(),
      unitPriceGrossCents: serviceLineGross.optional(),
      vatPercent: serviceLineVat.optional(),
      discountPercent: serviceLineDiscount.optional(),
    })
    .strict(),
  z
    .object({
      op: z.literal('part.add'),
      bookingServiceId: serviceLineId,
      newLineId: serviceLineId,
      name: serviceLineText(200),
      quantity: serviceLinePartQuantity,
      unitPriceGrossCents: serviceLineGross,
      vatPercent: serviceLineVat,
      discountPercent: serviceLineDiscount,
    })
    .strict(),
  z
    .object({ op: z.literal('part.remove'), bookingServiceId: serviceLineId, lineId: serviceLineId })
    .strict(),
  z
    .object({
      op: z.literal('extra.update'),
      bookingServiceId: serviceLineId,
      lineId: serviceLineId,
      description: serviceLineDescription.optional(),
      amountGrossCents: serviceLineGross.optional(),
      // Only on an existing per-unit extra (refused otherwise by the server).
      quantity: serviceLineExtraQuantity.optional(),
      unit: serviceLineText(20).optional(),
    })
    .strict(),
  z
    .object({
      // Taxable and fixed-quantity, as the portal adds an extra.
      op: z.literal('extra.add'),
      bookingServiceId: serviceLineId,
      newLineId: serviceLineId,
      description: serviceLineDescription,
      amountGrossCents: serviceLineGross,
    })
    .strict(),
  z
    .object({ op: z.literal('extra.remove'), bookingServiceId: serviceLineId, lineId: serviceLineId })
    .strict(),
  z
    .object({
      op: z.literal('service.update'),
      bookingServiceId: serviceLineId,
      // null clears the task notes, as the portal PUT allows.
      notes: serviceLineText(2000).nullable().optional(),
      // null returns to the catalog price; 0 is a genuine free price.
      fixedPriceOverrideGrossCents: serviceLineGross.nullable().optional(),
    })
    .strict(),
]);
export type ServiceLineOp = z.infer<typeof serviceLineOpSchema>;
export type ServiceLineKind = 'labor' | 'part' | 'extra';

export const serviceLineOpsSchema = z
  .array(serviceLineOpSchema)
  .min(1)
  .max(QUOTE_SERVICE_LINE_MAX_INPUT_OPS);

const serviceLineRevision = z.string().regex(/^sha256:[a-f0-9]{64}$/);

/** The base the orchestrator read: the review is refused (412) when the quote moved since. */
export const quoteServiceLineReviewRequestSchema = z
  .object({
    expectedBase: z
      .object({ quoteRevision: serviceLineRevision, updatedAt: z.string().datetime() })
      .strict(),
    ops: serviceLineOpsSchema,
  })
  .strict();

/** The consent token: the reviewed base and the reviewed resulting state. */
export const quoteServiceLineChangeTokenSchema = z
  .object({
    quoteRevision: serviceLineRevision,
    updatedAt: z.string().datetime(),
    resultRevision: serviceLineRevision,
  })
  .strict();

/** The reviewed write's body. Send `ops` exactly as the review returned them. */
export const reviewedQuoteServiceLineChangeSchema = z
  .object({ ops: serviceLineOpsSchema, review: quoteServiceLineChangeTokenSchema })
  .strict();

export type QuoteServiceLineNormalizationRefusal =
  | 'multiple_services'
  | 'line_kind_mismatch'
  | 'duplicate_line_id'
  | 'update_fields_missing'
  | 'update_after_remove'
  | 'line_already_removed'
  | 'extra_not_per_unit'
  | 'no_operations'
  | 'too_many_operations';

export type QuoteServiceLineNormalization =
  | { ok: true; ops: ServiceLineOp[] }
  | { ok: false; reason: QuoteServiceLineNormalizationRefusal };

type AnyUpdate = Extract<ServiceLineOp, { lineId: string }>;
type AnyAdd = Extract<ServiceLineOp, { newLineId: string }>;
type ServiceUpdate = Extract<ServiceLineOp, { op: 'service.update' }>;
const SERVICE_LINE_KINDS: readonly ServiceLineKind[] = ['labor', 'part', 'extra'];

function serviceLineKindOf(op: ServiceLineOp): ServiceLineKind | 'service' {
  return op.op.slice(0, op.op.indexOf('.')) as ServiceLineKind | 'service';
}

/** The changed fields an operation carries (keys other than op and ids, not undefined). */
export function serviceLineOpFields(op: ServiceLineOp): string[] {
  return Object.entries(op)
    .filter(
      ([key, value]) =>
        value !== undefined &&
        key !== 'op' &&
        key !== 'bookingServiceId' &&
        key !== 'lineId' &&
        key !== 'newLineId'
    )
    .map(([key]) => key);
}

/** The operation's own field values, without op and ids; undefined keys are dropped. */
function serviceLineFieldValues(op: ServiceLineOp): Record<string, unknown> {
  return Object.fromEntries(
    serviceLineOpFields(op).map((key) => [key, (op as Record<string, unknown>)[key]])
  );
}

/**
 * Pure normalization (design §4.3). staff-api is authoritative; the
 * orchestrator repeats it for the card text.
 *
 * - One service per list.
 * - update + update of one line and field: the later value wins.
 * - An update of a pending add folds into the add; a remove of a pending add
 *   drops both.
 * - An update after a remove is refused; so is a field-less update.
 * - At most 20 operations remain.
 *
 * The output is canonical: the service update first, then per kind (labour,
 * parts, extras) the removals and updates sorted by line id, then the adds in
 * the order they were made (their order is the row order).
 */
export function normalizeServiceLineOps(
  input: readonly ServiceLineOp[]
): QuoteServiceLineNormalization {
  const first = input[0];
  if (!first) return { ok: false, reason: 'no_operations' };
  let service: ServiceUpdate | undefined;
  const kindById = new Map<string, ServiceLineKind>();
  const adds = new Map<string, AnyAdd>();
  const updates = new Map<string, AnyUpdate>();
  const removes = new Map<string, AnyUpdate>();
  const droppedAdds = new Set<string>();
  for (const op of input) {
    if (op.bookingServiceId !== first.bookingServiceId)
      return { ok: false, reason: 'multiple_services' };
    if (op.op === 'service.update') {
      if (serviceLineOpFields(op).length === 0)
        return { ok: false, reason: 'update_fields_missing' };
      service = { ...(service ?? op), ...serviceLineFieldValues(op) } as ServiceUpdate;
      continue;
    }
    const kind = serviceLineKindOf(op) as ServiceLineKind;
    const id = 'newLineId' in op ? op.newLineId : op.lineId;
    const known = kindById.get(id);
    if (known !== undefined && known !== kind) return { ok: false, reason: 'line_kind_mismatch' };
    kindById.set(id, kind);
    if ('newLineId' in op) {
      if (adds.has(id) || updates.has(id) || removes.has(id) || droppedAdds.has(id))
        return { ok: false, reason: 'duplicate_line_id' };
      adds.set(id, op);
    } else if (op.op.endsWith('.remove')) {
      if (droppedAdds.has(id) || removes.has(id))
        return { ok: false, reason: 'line_already_removed' };
      if (adds.has(id)) {
        adds.delete(id);
        droppedAdds.add(id);
      } else {
        updates.delete(id);
        removes.set(id, op);
      }
    } else {
      if (serviceLineOpFields(op).length === 0)
        return { ok: false, reason: 'update_fields_missing' };
      if (removes.has(id) || droppedAdds.has(id))
        return { ok: false, reason: 'update_after_remove' };
      const add = adds.get(id);
      if (add) {
        if (op.op === 'extra.update' && (op.quantity !== undefined || op.unit !== undefined))
          return { ok: false, reason: 'extra_not_per_unit' };
        adds.set(id, { ...add, ...serviceLineFieldValues(op) } as AnyAdd);
      } else {
        const previous = updates.get(id);
        updates.set(id, { ...(previous ?? op), ...serviceLineFieldValues(op) } as AnyUpdate);
      }
    }
  }
  const byId = (a: AnyUpdate, b: AnyUpdate) => (a.lineId < b.lineId ? -1 : a.lineId > b.lineId ? 1 : 0);
  const ops: ServiceLineOp[] = service ? [service] : [];
  for (const kind of SERVICE_LINE_KINDS) {
    const ofKind = (op: ServiceLineOp) => serviceLineKindOf(op) === kind;
    ops.push(...[...removes.values()].filter(ofKind).sort(byId));
    ops.push(...[...updates.values()].filter(ofKind).sort(byId));
    ops.push(...[...adds.values()].filter(ofKind));
  }
  if (ops.length === 0) return { ok: false, reason: 'no_operations' };
  if (ops.length > QUOTE_SERVICE_LINE_MAX_OPS) return { ok: false, reason: 'too_many_operations' };
  return { ok: true, ops };
}

/**
 * Syntactic superset of "changes billable hours": any labour add or removal,
 * and any labour update that carries hours. Exact for the `ops` a review
 * returns (unchanged fields are dropped there), so a route preHandler can
 * re-check `work_orders.edit_billable_hours` before an idempotent replay.
 */
export function serviceLineOpsMayChangeBillableHours(ops: readonly ServiceLineOp[]): boolean {
  return ops.some(
    (op) =>
      op.op === 'labor.add' ||
      op.op === 'labor.remove' ||
      (op.op === 'labor.update' && op.estimatedHours !== undefined)
  );
}

const serviceLineReviewService = quoteReviewSchema.shape.services.element;
const serviceLineLaborRow = serviceLineReviewService.shape.laborItems.element;
const serviceLinePartRow = serviceLineReviewService.shape.parts.element;
const serviceLineExtraRow = serviceLineReviewService.shape.extras.element;
const serviceLineServiceRow = z
  .object({ notes: z.string().nullable(), fixedPriceOverrideCents: z.number().finite().nullable() })
  .strict();
const serviceLineChangeKind = z.enum(['added', 'updated', 'removed']);
const serviceLineChangedField = z.enum([
  'added',
  'removed',
  'description',
  'estimatedHours',
  'discountPercent',
  'name',
  'quantity',
  'unitPriceCents',
  'vatPercent',
  'amountCents',
  'unit',
  'notes',
  'fixedPriceOverrideCents',
]);
const serviceLineChangeBase = {
  lineId: serviceLineId,
  change: serviceLineChangeKind,
  changedFields: z.array(serviceLineChangedField).min(1),
};
const serviceLinePricingMode = z.enum(['fixed', 'components']);

/** Review notes the card states when they apply (design §5.6). */
export const quoteServiceLineReviewNoteSchema = z.enum([
  'fixed_price_lines_do_not_change_total',
  'pricing_mode_to_components',
  'pricing_mode_to_fixed',
  'labor_rate_zero',
  'surcharge_changed',
  'planned_duration_changed',
  'queue_may_shift',
  'billable_hours_changed',
  'per_unit_extra_without_quantity_billed_as_one',
]);

/**
 * Server-built review of one operation list (version 1). `quote` is the
 * current complete snapshot; `result` the complete resulting services and
 * pricing; `resultRevision` the revision of the resulting snapshot with the
 * reviewed base `updatedAt`. A missing or unreadable review must disable
 * confirmation.
 */
export const quoteServiceLineChangeReviewSchema = z
  .object({
    version: z.literal(1),
    bookingServiceId: serviceLineId,
    ops: z.array(serviceLineOpSchema).min(1).max(QUOTE_SERVICE_LINE_MAX_OPS),
    quote: quoteReviewSchema,
    changes: z
      .array(
        z.discriminatedUnion('kind', [
          z
            .object({
              kind: z.literal('labor'),
              ...serviceLineChangeBase,
              before: serviceLineLaborRow.nullable(),
              after: serviceLineLaborRow.nullable(),
            })
            .strict(),
          z
            .object({
              kind: z.literal('part'),
              ...serviceLineChangeBase,
              before: serviceLinePartRow.nullable(),
              after: serviceLinePartRow.nullable(),
            })
            .strict(),
          z
            .object({
              kind: z.literal('extra'),
              ...serviceLineChangeBase,
              before: serviceLineExtraRow.nullable(),
              after: serviceLineExtraRow.nullable(),
            })
            .strict(),
          z
            .object({
              kind: z.literal('service'),
              ...serviceLineChangeBase,
              before: serviceLineServiceRow,
              after: serviceLineServiceRow,
            })
            .strict(),
        ])
      )
      .min(1),
    result: z
      .object({
        services: quoteReviewSchema.shape.services,
        pricing: quoteReviewSchema.shape.pricing,
      })
      .strict(),
    derived: z
      .object({
        services: z.array(
          z
            .object({
              bookingServiceId: serviceLineId,
              plannedHoursBefore: z.number().nullable(),
              plannedHoursAfter: z.number().nullable(),
              pricingModeBefore: serviceLinePricingMode,
              pricingModeAfter: serviceLinePricingMode,
            })
            .strict()
        ),
        estimatedDurationHoursBefore: z.number().nullable(),
        estimatedDurationHoursAfter: z.number().nullable(),
        queueMayShift: z.boolean(),
        billableHoursChanged: z.boolean(),
      })
      .strict(),
    notes: z.array(quoteServiceLineReviewNoteSchema),
    draftRows: z
      .array(
        z
          .object({
            newLineId: serviceLineId,
            bookingServiceId: serviceLineId,
            kind: z.enum(['labor', 'part', 'extra']),
            label: z.string(),
          })
          .strict()
      )
      .max(QUOTE_SERVICE_LINE_MAX_OPS),
    resultRevision: serviceLineRevision,
  })
  .strict()
  .superRefine((review, ctx) => {
    if (review.ops.some((op) => op.bookingServiceId !== review.bookingServiceId))
      ctx.addIssue({ code: 'custom', message: 'operations target another service' });
    if (!review.result.services.some((s) => s.bookingServiceId === review.bookingServiceId))
      ctx.addIssue({ code: 'custom', message: 'resulting services omit the edited service' });
    const adds = review.ops.flatMap((op) => ('newLineId' in op ? [op.newLineId] : []));
    if (
      review.draftRows.length !== adds.length ||
      review.draftRows.some(
        (row) => !adds.includes(row.newLineId) || row.bookingServiceId !== review.bookingServiceId
      )
    )
      ctx.addIssue({ code: 'custom', message: 'draft rows do not match the added lines' });
    const currentService = review.quote.services.find(s => s.bookingServiceId === review.bookingServiceId);
    const resultService = review.result.services.find(s => s.bookingServiceId === review.bookingServiceId);
    if (!currentService || !resultService || review.changes.length !== review.ops.length)
      ctx.addIssue({ code: 'custom', message: 'review omits changes or the current service' });
    const seen = new Set<string>();
    for (const change of review.changes) {
      const removed = change.change === 'removed';
      const added = change.change === 'added';
      const operation = review.ops.find(op => op.op.split('.')[0] === change.kind &&
        ('lineId' in op ? op.lineId : 'newLineId' in op ? op.newLineId : op.bookingServiceId) === change.lineId);
      const expectedChange = operation?.op.endsWith('.add') ? 'added' : operation?.op.endsWith('.remove') ? 'removed' : 'updated';
      const key = `${change.kind}:${change.lineId}`;
      if (!operation || change.change !== expectedChange || seen.has(key))
        ctx.addIssue({ code: 'custom', message: 'changes do not match operations' });
      seen.add(key);
      if (change.kind !== 'service' && (removed !== (change.after === null) || added !== (change.before === null)))
        ctx.addIssue({ code: 'custom', message: 'change rows do not match the change kind' });
      if (change.kind !== 'service') {
        const collection = change.kind === 'labor' ? 'laborItems' : change.kind === 'part' ? 'parts' : 'extras';
        const before = currentService?.[collection].find(row => row.id === change.lineId) ?? null;
        const after = resultService?.[collection].find(row => row.id === change.lineId) ?? null;
        if (JSON.stringify(before) !== JSON.stringify(change.before) || JSON.stringify(after) !== JSON.stringify(change.after))
          ctx.addIssue({ code: 'custom', message: 'change rows do not match the complete snapshots' });
      } else if (change.lineId !== review.bookingServiceId ||
        JSON.stringify(change.before) !== JSON.stringify({ notes: currentService?.notes, fixedPriceOverrideCents: currentService?.fixedPriceOverrideCents }) ||
        JSON.stringify(change.after) !== JSON.stringify({ notes: resultService?.notes, fixedPriceOverrideCents: resultService?.fixedPriceOverrideCents }))
        ctx.addIssue({ code: 'custom', message: 'service change does not match the complete snapshots' });
    }
    if (new Set(review.draftRows.map(row => row.newLineId)).size !== review.draftRows.length ||
      review.draftRows.some(row => !review.ops.some(op => 'newLineId' in op && op.newLineId === row.newLineId && op.op === `${row.kind}.add`)))
      ctx.addIssue({ code: 'custom', message: 'draft row identities are inconsistent' });
    if (review.result.pricing.netSubtotalCents + review.result.pricing.taxCents !== review.result.pricing.grossTotalCents)
      ctx.addIssue({ code: 'custom', message: 'result totals are inconsistent' });
  });

/** The reviewed write's 200 body. */
export const quoteServiceLineChangeResultSchema = z
  .object({
    workOrderId: z.string().uuid(),
    bookingServiceId: serviceLineId,
    updatedAt: z.string().datetime(),
    changedLineIds: z.array(serviceLineId).min(1),
    totals: z
      .object({
        netSubtotalCents: z.number().int(),
        taxCents: z.number().int(),
        grossTotalCents: z.number().int(),
      })
      .strict(),
  })
  .strict();

export type QuoteServiceLineReviewRequest = z.infer<typeof quoteServiceLineReviewRequestSchema>;
export type QuoteServiceLineChangeToken = z.infer<typeof quoteServiceLineChangeTokenSchema>;
export type ReviewedQuoteServiceLineChange = z.infer<typeof reviewedQuoteServiceLineChangeSchema>;
export type QuoteServiceLineChangeReview = z.infer<typeof quoteServiceLineChangeReviewSchema>;
export type QuoteServiceLineChangeResult = z.infer<typeof quoteServiceLineChangeResultSchema>;
export type QuoteServiceLineReviewNote = z.infer<typeof quoteServiceLineReviewNoteSchema>;

const serviceAddId = z.string().uuid();
const serviceAddRevision = z.string().regex(/^sha256:[a-f0-9]{64}$/);
export const quoteServiceAddCommandSchema = z
  .object({
    serviceId: serviceAddId,
    newBookingServiceId: serviceAddId,
  })
  .strict();
export const quoteServiceAddTokenSchema = z
  .object({
    quoteRevision: serviceAddRevision,
    updatedAt: z.string().datetime(),
    resultRevision: serviceAddRevision,
    catalogRevision: serviceAddRevision,
  })
  .strict();
export const quoteServiceAddReviewRequestSchema = z
  .object({
    command: quoteServiceAddCommandSchema,
    expectedBase: quoteServiceAddTokenSchema.omit({ resultRevision: true, catalogRevision: true }),
  })
  .strict();
export const reviewedQuoteServiceAddSchema = z
  .object({
    command: quoteServiceAddCommandSchema,
    review: quoteServiceAddTokenSchema,
  })
  .strict();
/** One catalog attachment, with complete before/after scope and canonical pricing. */
export const quoteServiceAddReviewSchema = z
  .object({
    version: z.literal(1),
    command: quoteServiceAddCommandSchema,
    quote: quoteReviewSchema,
    result: quoteReviewSchema,
    catalogRevision: serviceAddRevision,
    summaryBefore: z.string().nullable(),
    summaryAfter: z.string(),
    catalogParts: z.array(
      z.object({ name: z.string(), isRequired: z.boolean(), partCategoryId: serviceAddId })
    ),
  })
  .strict()
  .superRefine((r, context) => {
    const added = r.result.services[r.quote.services.length];
    const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const { estimatedDurationHours: _beforeHours, ...beforeSchedule } = r.quote.schedule;
    const { estimatedDurationHours: _afterHours, ...afterSchedule } = r.result.schedule;
    const fields = [
      'id',
      'workOrderNumber',
      'status',
      'updatedAt',
      'workshopTimezone',
      'quoteExpiresAt',
      'isDisputed',
      'allowedActions',
      'vehicle',
      'orderer',
      'parties',
      'resources',
      'customerNotes',
      'legacyItems',
      'legacyParts',
    ] as const;
    if (
      r.quote.status !== 'quote' ||
      r.quote.isDisputed ||
      r.quote.legacyItems.length ||
      r.quote.legacyParts.length ||
      r.quote.resources.assignedMechanicId ||
      r.quote.resources.assignedLiftId ||
      r.quote.services.some(
        (service) =>
          service.assignedMechanicId !== null ||
          service.assignedLiftId !== null ||
          service.scheduledAt !== null
      ) ||
      r.quote.services.some(
        (service) => service.bookingServiceId === r.command.newBookingServiceId
      ) ||
      !same(r.quote.services, r.result.services.slice(0, r.quote.services.length)) ||
      new Set(r.result.services.map((service) => service.bookingServiceId)).size !==
        r.result.services.length ||
      r.result.services.length !== r.quote.services.length + 1 ||
      added?.bookingServiceId !== r.command.newBookingServiceId ||
      added.serviceId !== r.command.serviceId ||
      added.assignedMechanicId ||
      added.assignedLiftId ||
      added.scheduledAt ||
      (r.quote.services.length > 0 && !r.summaryBefore) ||
      r.summaryAfter !==
        (r.quote.services.length ? `${r.summaryBefore ?? ''}, ${added.name}` : added.name) ||
      fields.some((field) => !same(r.quote[field], r.result[field])) ||
      !same(beforeSchedule, afterSchedule) ||
      r.quote.pricing.laborRateCents !== r.result.pricing.laborRateCents ||
      r.quote.pricing.defaultVatPercent !== r.result.pricing.defaultVatPercent ||
      !same(r.quote.pricing.surcharge, r.result.pricing.surcharge) ||
      r.result.pricing.netSubtotalCents + r.result.pricing.taxCents !==
        r.result.pricing.grossTotalCents
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Attachment review changes unrelated scope or does not match its command',
      });
    try {
      new Intl.DateTimeFormat('en', { timeZone: r.quote.workshopTimezone });
    } catch {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid workshop timezone' });
    }
  });
export const quoteServiceAddResultSchema = z
  .object({
    workOrderId: serviceAddId,
    bookingServiceId: serviceAddId,
    serviceId: serviceAddId,
    updatedAt: z.string().datetime(),
    totals: z
      .object({
        netSubtotalCents: z.number().int(),
        taxCents: z.number().int(),
        grossTotalCents: z.number().int(),
      })
      .strict(),
  })
  .strict();
export type QuoteServiceAddCommand = z.infer<typeof quoteServiceAddCommandSchema>;
export type QuoteServiceAddToken = z.infer<typeof quoteServiceAddTokenSchema>;
export type QuoteServiceAddReview = z.infer<typeof quoteServiceAddReviewSchema>;
export type QuoteServiceAddResult = z.infer<typeof quoteServiceAddResultSchema>;
